import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { OrderItemVm, OrderItemStatus } from 'api/reception/order-item.vm';
import { GetOrderItemsVm } from 'api/reception/get-order-items.vm';
import { Error, Pending, ArrowBack } from '@mui/icons-material';
import { OrderVm, OrdersStatus } from 'api/reception/order.vm';
import { getOrderItems } from 'api/reception/get-order-items';
import { useNavigate, useParams } from 'react-router-dom';
import { GetOrderVm } from 'api/reception/get-order.vm';
import { getOrder } from 'api/reception/get-order';

const PurchaseOrderPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [orderItems, setOrderItems] = useState<OrderItemVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [orderDetails, setOrderDetails] = useState<OrderVm | null>(null);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchOrderItems = (offset: string | null, reset: boolean = false) => {
    if (!orderId) return;

    setLoading(true);

    getOrderItems(orderId, offset || '', {
      success: (response: GetOrderItemsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setOrderItems(reset ? response.objects : [...orderItems, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter itens da encomenda:', err);
        setLoading(false);
      },
    });
  };

  const fetchOrderDetails = () => {
    if (!orderId) return;

    setLoadingDetails(true);

    getOrder(orderId, {
      success: (response: GetOrderVm) => {
        setOrderDetails(response);
        setLoadingDetails(false);
      },
      error: (err) => {
        console.error('Falha ao obter detalhes da encomenda:', err);
        setLoadingDetails(false);
      },
    });
  };

  useEffect(() => {
    fetchOrderItems(null, true);
    fetchOrderDetails();
  }, [orderId]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchOrderItems(nextOffset);
  };

  const getStatusChip = (status: OrderItemStatus) => {
    switch (status) {
      case OrderItemStatus.OPEN:
        return <Chip icon={<Pending fontSize="small" />} label="Aberto" color="info" size="small" />;
      case OrderItemStatus.CLOSED:
        return <Chip icon={<Error fontSize="small" />} label="Fechado" color="error" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  const getOrderStatusChip = (status: OrdersStatus) => {
    switch (status) {
      case OrdersStatus.PENDING:
        return <Chip icon={<Pending fontSize="small" />} label="Pendente" color="info" size="small" />;
      case OrdersStatus.CLOSED:
        return <Chip icon={<Error fontSize="small" />} label="Fechada" color="error" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation}>
            Encomendas de Fornecedor
          </Link>
          <Typography color="text.primary">Encomenda {orderId}</Typography>
        </Breadcrumbs>
      </Box>

      {loadingDetails ? (
        <CircularProgress />
      ) : orderDetails ? (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes da Encomenda
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                ID
              </Typography>
              <Typography>{orderDetails.id}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Série
              </Typography>
              <Typography>{orderDetails.serieId}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Fornecedor
              </Typography>
              <Typography>{orderDetails.supplierName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Criada Em
              </Typography>
              <Typography>{orderDetails.orderDate ? new Date(orderDetails.orderDate).toLocaleDateString('pt-PT') : '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Estado
              </Typography>
              <Typography>{getOrderStatusChip(orderDetails.status)}</Typography>
            </Box>
          </Box>
          {orderDetails.observations && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Observações
              </Typography>
              <Typography>{orderDetails.observations}</Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Typography variant="body2" color="error">
          Falha ao carregar detalhes da encomenda
        </Typography>
      )}

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Itens da Encomenda
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Armazém</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Satisfeito</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderItems.map((item) => (
              <TableRow key={`${item.orderId}-${item.productId}`} hover>
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.productSku}</TableCell>
                <TableCell>{item.warehouseId}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{item.satisfiedQuantity}</TableCell>
                <TableCell>{getStatusChip(item.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : orderItems.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais itens para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum item encontrado
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PurchaseOrderPage;
